import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import me from "models/me";
import CircularProgress from "material-ui/CircularProgress";
import Loading from "components/loading";
import { Form } from "formsy-react";
import Input from "components/form/text-field";
import Button from "components/form/button";
import ChevronIcon from "material-ui/svg-icons/navigation/chevron-right";
import { DATATEST } from "constants/integration";
import { Map } from "immutable";
import * as validators from "lib/validators";
import { checkAuthenticationFlow, lsOneLoginRedirect } from "lib/lsid";
import { ErrorTypes, FlowTypes } from "lib/lsid/enums";
import { FormTitleLogo } from "./form-title-logo";
import { LoginFormWrapper } from ".";
import { buildAndSubmitHiddenForm } from "./utils";

interface ILoginFormProps {
    model?: any;
    siteLogo?: string;
    siteRegURL?: string;
    redirect?: string;
    onResetState: () => void;
    authentication: any;
    onSwitchLoginFlow: (flow: FlowTypes) => void;
    error?: string;
    setSharedEmail: (email?: string) => void;
    sharedEmail: string;
    isMobile: boolean;
}

interface ILoginFormState {
    loading: boolean;
    error?: string;
    email?: string;
    errors: any;
}

export class LoginFormSSO extends React.Component<ILoginFormProps, ILoginFormState> {
    loading = false;
    _form: any;

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            email: props.sharedEmail,
            error: props.error,
            errors: Map(),
        };
    }

    validateForm = () => {
        let valid = true;
        const { email } = this.state;
        let newErrors = this.state.errors;
        if (!email) {
            newErrors = newErrors.set("email", formatMessage("This field is required."));
            valid = false;
        } else if (!validators.default.email(email)) {
            newErrors = newErrors.set("email", formatMessage("Enter a valid email address."));
            valid = false;
        }

        this.setState({ errors: newErrors });

        return valid;
    };

    handleSubmitButtonTap = () => {
        if (this.validateForm()) {
            this.handleSubmit();
        }
    };

    handleEmailChange = (event: any, value: string) => {
        this.setState({ email: value });
        if (!value) {
            this.setState({ errors: this.state.errors.set("email", formatMessage("This field is required.")) });
        } else {
            this.setState({ errors: this.state.errors.delete("email") });
        }
    };

    handleLegacyLoginFlow = () => {
        this.props.setSharedEmail(this.state.email);
        this.props.onSwitchLoginFlow(FlowTypes.LEGACY);
    };

    handleSubmit = async () => {
        const { email } = this.state;
        let { redirect } = this.props;

        if (!redirect && this.props.model) {
            redirect = `/brand/${this.props.model._id}`;
        }

        if (lsOneLoginRedirect(email, redirect)) {
            return null;
        }

        if (!this.loading) {
            try {
                const credentials = { email, redirect_after_login: redirect };
                const { type } = await checkAuthenticationFlow({ email, redirect_after_login: redirect });

                if (type === FlowTypes.SSO) {
                    this.setState({ loading: true });
                    this.loading = true;
                    buildAndSubmitHiddenForm(credentials);
                }
            } catch (e) {
                const errorMessage = (() => {
                    if (!e.error) {
                        return formatMessage("Oops! Something went wrong. Please refresh and try again.");
                    }
                    return e.error.type !== ErrorTypes.LSID_NOT_AUTHORIZED
                        ? e.error.message
                        : formatMessage("Your email is not associated with the SSO Account.");
                })();

                this.setState({ error: errorMessage });
                this.setState({ loading: false });
                this.loading = false;
            }
        }
    };

    render() {
        const { model, redirect, siteLogo, isMobile } = this.props;
        const { error, errors, email } = this.state;
        if (me.isSignedIn() && !redirect) {
            return <Loading />;
        }

        return (
            <LoginFormWrapper data-test={DATATEST.LOGIN.LOGIN_FORM} className={model ? "branded" : undefined}>
                <FormTitleLogo siteLogo={siteLogo} model={model} />
                {this.state.loading && (
                    <div className="loading">
                        {/* check styling with loadingProgress */}
                        <CircularProgress />
                    </div>
                )}

                <Form ref={(ref) => (this._form = ref)}>
                    {error && (
                        <div data-test={DATATEST.LOGIN.ERROR} className="alert alert-error" role="alert">
                            {error}
                        </div>
                    )}
                    <fieldset className="formset">
                        <Input
                            name="email"
                            value={email ?? ""}
                            disabled={this.state.loading}
                            placeholder={formatMessage("Email for Single Sign On")}
                            errorText={errors.get("email") ? errors.get("email") : null}
                            inputStyle={{ background: "white" }}
                            style={{ margin: "24px 0 0" }}
                            allowWhitespace={false}
                            large={true}
                            fullWidth={true}
                            dataTest={DATATEST.LOGIN.EMAIL}
                            onChange={this.handleEmailChange}
                        />
                    </fieldset>
                    <fieldset className="formset">
                        <Button
                            type="submit"
                            onClick={this.handleSubmitButtonTap}
                            disabled={this.state.loading}
                            data-test={DATATEST.LOGIN.SUBMIT}
                            label={formatMessage("Log In with Single Sign On")}
                            labelPosition="before"
                            icon={<ChevronIcon color="white" />}
                            className="loginSubmit"
                            primary={true}
                            large={true}
                            style={{ marginTop: "16px" }}
                        />
                    </fieldset>
                    {!isMobile && (
                        <div className="option-link">
                            <a data-test={DATATEST.LOGIN.SWITCH_NORMAL} onClick={this.handleLegacyLoginFlow}>
                                {formatMessage("Log In using your email")}
                            </a>
                        </div>
                    )}
                </Form>
            </LoginFormWrapper>
        );
    }
}

export default LoginFormSSO;
